import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { contactUsService, ContactUsListResponse, ContactUsMessage } from '../../../services/contactUs';
import moment from 'moment-jalaali';
import Modal from '../../../components/common/Modal';
import { UAParser } from 'ua-parser-js';

const ContactUsManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ContactUsListResponse | null>(null);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<ContactUsMessage | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
        load();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search]);

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
      navigate('/dashboard', { replace: true });
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await contactUsService.getAll(page, 10, search);
      setList(res);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('آیا از حذف این پیام اطمینان دارید؟')) return;
    setDeletingId(id);
    try {
      await contactUsService.delete(id);
      load();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">مدیریت پیام‌های تماس با ما</h1>
          <p className="text-gray-500 mt-1">لیست پیام‌های ارسالی کاربران</p>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative md:col-span-2">
            <input 
              className="input-field pl-10" 
              placeholder="جستجو (شماره تماس یا متن پیام)" 
              value={search} 
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} 
            />
            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">شماره تماس</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">پیام</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">IP</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">دستگاه</th>
                <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">تاریخ</th>
                <th className="text-center py-4 px-6 text-sm font-semibold text-gray-600 w-20">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">در حال بارگذاری...</td>
                </tr>
              ) : (list?.data || []).length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-500">هیچ پیامی یافت نشد</td>
                </tr>
              ) : (
                (list?.data || []).map((item) => (
                  <tr 
                    key={item.id} 
                    onClick={() => setSelectedMessage(item)}
                    className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                  >
                    <td className="py-4 px-6 text-sm font-medium text-gray-900 dir-ltr text-right">{item.phone_number}</td>
                    <td className="py-4 px-6 text-sm text-gray-600 max-w-xs truncate" title={item.description}>{item.description}</td>
                    <td className="py-4 px-6 text-sm text-gray-500 dir-ltr text-right">{item.ip}</td>
                    <td className="py-4 px-6 text-sm text-gray-500 max-w-[150px] truncate" title={item.user_agent}>{item.user_agent}</td>
                    <td className="py-4 px-6 text-sm text-gray-500 dir-ltr text-right">
                      {moment(item.created_at).format('jYYYY/jMM/jDD HH:mm')}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-center">
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(item.id);
                          }}
                          disabled={deletingId === item.id}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-50"
                          title="حذف"
                        >
                          {deletingId === item.id ? (
                            <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {list && list.meta.total_pages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-100">
            <div className="text-sm text-gray-500">
              نمایش {((page - 1) * 10) + 1} تا {Math.min(page * 10, list.meta.total_items)} از {list.meta.total_items} مورد
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, list.meta.total_pages) }, (_, i) => {
                  let p = i + 1;
                  if (list.meta.total_pages > 5) {
                     if (page > 3) p = page - 2 + i;
                     if (p > list.meta.total_pages) p = list.meta.total_pages - (4 - i);
                  }
                  return (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        page === p 
                          ? 'bg-[#0e444e] text-white' 
                          : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage(p => Math.min(list.meta.total_pages, p + 1))}
                disabled={page === list.meta.total_pages}
                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal 
        isOpen={!!selectedMessage} 
        onClose={() => setSelectedMessage(null)}
        title="جزئیات پیام"
      >
        {selectedMessage && (
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">شماره تماس</label>
                    <div className="text-gray-900 text-lg dir-ltr text-right">{selectedMessage.phone_number}</div>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">تاریخ ثبت</label>
                    <div className="text-gray-900 dir-ltr text-right">
                        {moment(selectedMessage.created_at).format('jYYYY/jMM/jDD HH:mm')}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">متن پیام</label>
                    <div className="bg-gray-50 rounded-lg p-3 text-gray-700 text-sm leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto border border-gray-100">
                        {selectedMessage.description}
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">IP Address</label>
                        <div className="text-gray-600 text-sm dir-ltr text-right font-mono">{selectedMessage.ip}</div>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-400 mb-1">سیستم کاربر</label>
                        {(() => {
                             const parser = new UAParser(selectedMessage.user_agent);
                             const browser = parser.getBrowser();
                             const os = parser.getOS();
                             const device = parser.getDevice();
                             const cpu = parser.getCPU();
                             
                             return (
                                 <div className="text-gray-700 text-sm dir-ltr text-right flex flex-col gap-0.5">
                                     <div className="font-medium">
                                         {browser.name || 'Unknown Browser'} {browser.version}
                                     </div>
                                     <div className="text-gray-500 text-xs">
                                         {os.name || 'Unknown OS'} {os.version}
                                     </div>
                                     {(device.vendor || device.model) && (
                                        <div className="text-gray-400 text-xs">
                                            {device.vendor} {device.model} {device.type ? `(${device.type})` : ''}
                                        </div>
                                     )}
                                     {cpu.architecture && (
                                         <div className="text-gray-300 text-[10px] uppercase">
                                             {cpu.architecture}
                                         </div>
                                     )}
                                 </div>
                             );
                        })()}
                    </div>
                </div>

                 <div className="pt-4 mt-4 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={() => setSelectedMessage(null)}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                    >
                        بستن
                    </button>
                </div>
            </div>
        )}
      </Modal>
    </div>
  );
};

export default ContactUsManagementPage;
